import { useState } from 'react';
import { Screen } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/Button';
import { LinkButton } from '@/components/ui/LinkButton';
import { Banner, Card, CardTitle } from '@/components/ui/Card';
import { Field, RangeField, Select, Toggle } from '@/components/ui/Form';
import { useAppData } from '@/services/data/dataContext';
import { useAuth } from '@/services/auth/authContext';
import { useVoices } from '@/hooks/useVoices';
import { speechEngine } from '@/services/speech/SpeechEngine';
import { selectUsableVoices } from '@/services/speech/voiceAssignment';
import { isRecognitionSupported } from '@/services/speech/recognition';
import { clearAllLocalData } from '@/services/storage/db';
import type { FontScale, ThemeMode } from '@/types/domain';

export function SettingsScreen() {
  const { settings, updateSettings, online, pendingWrites, syncNow } = useAppData();
  const { user, signOut } = useAuth();
  const { voices, supported } = useVoices();
  const [confirmingReset, setConfirmingReset] = useState(false);

  const usable = selectUsableVoices(voices);

  const previewVoice = () => {
    speechEngine.prime();
    void speechEngine.speak('What a damned epicurean rascal is this!', {
      voiceURI: settings.defaultVoiceURI,
      rate: settings.rehearsalSpeed,
      volume: settings.volume,
    });
  };

  const resetLocalData = async () => {
    await clearAllLocalData();
    globalThis.location.reload();
  };

  return (
    <>
      <TopBar title="Settings" subtitle={user?.email ?? undefined} />
      <Screen>
        <Card className="flex flex-col gap-1">
          <CardTitle>Rehearsal</CardTitle>
          <RangeField
            label="Speech speed"
            value={settings.rehearsalSpeed}
            min={0.5}
            max={2}
            step={0.05}
            format={(value) => `${value.toFixed(2)}×`}
            onChange={(rehearsalSpeed) => void updateSettings({ rehearsalSpeed })}
          />
          <RangeField
            label="Pause length"
            value={settings.pauseMs}
            min={0}
            max={10000}
            step={250}
            format={(value) => `${(value / 1000).toFixed(2)}s`}
            onChange={(pauseMs) => void updateSettings({ pauseMs })}
          />
          <RangeField
            label="Hesitation threshold"
            value={settings.hesitationThresholdMs}
            min={1000}
            max={15000}
            step={500}
            format={(value) => `${(value / 1000).toFixed(1)}s`}
            onChange={(hesitationThresholdMs) => void updateSettings({ hesitationThresholdMs })}
          />
          <RangeField
            label="Cue length"
            value={settings.cueWordCount}
            min={2}
            max={20}
            step={1}
            format={(value) => `${value} words`}
            onChange={(cueWordCount) => void updateSettings({ cueWordCount })}
          />
          <Toggle
            label="Auto-reveal after the pause"
            description="Shows the line automatically, but still waits for you to grade yourself."
            checked={settings.autoAdvance}
            onChange={(autoAdvance) => void updateSettings({ autoAdvance })}
          />
          <Toggle
            label="Listen to my lines"
            description={
              isRecognitionSupported()
                ? 'Grades what you say and moves on by itself when you get it right. Audio stays on this device.'
                : 'Not supported in this browser.'
            }
            checked={settings.listeningEnabled && isRecognitionSupported()}
            onChange={(listeningEnabled) => void updateSettings({ listeningEnabled })}
          />
          <Toggle
            label="Read stage directions"
            checked={settings.speakStageDirections}
            onChange={(speakStageDirections) => void updateSettings({ speakStageDirections })}
          />
        </Card>

        <Card className="flex flex-col gap-1">
          <CardTitle>Voice</CardTitle>
          {!supported ? (
            <Banner tone="warn">This browser cannot read lines aloud.</Banner>
          ) : (
            <>
              <RangeField
                label="Volume"
                value={settings.volume}
                min={0}
                max={1}
                step={0.05}
                format={(value) => `${Math.round(value * 100)}%`}
                onChange={(volume) => void updateSettings({ volume })}
              />
              <Field
                label="Default voice"
                htmlFor="default-voice"
                hint="Each character gets a distinct voice automatically. On iPhone, characters are separated by pitch instead."
              >
                <Select
                  id="default-voice"
                  value={settings.defaultVoiceURI ?? ''}
                  onChange={(event) =>
                    void updateSettings({ defaultVoiceURI: event.target.value || null })
                  }
                >
                  <option value="">System default</option>
                  {usable.map((voice) => (
                    <option key={voice.voiceURI} value={voice.voiceURI}>
                      {voice.name} ({voice.lang})
                    </option>
                  ))}
                </Select>
              </Field>
              <Button variant="secondary" onClick={previewVoice}>
                Preview voice
              </Button>
            </>
          )}
        </Card>

        <Card className="flex flex-col gap-1">
          <CardTitle>Appearance & accessibility</CardTitle>
          <Field label="Theme" htmlFor="theme">
            <Select
              id="theme"
              value={settings.theme}
              onChange={(event) => void updateSettings({ theme: event.target.value as ThemeMode })}
            >
              <option value="system">Match my device</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </Select>
          </Field>
          <Field label="Text size" htmlFor="font-scale">
            <Select
              id="font-scale"
              value={settings.fontScale}
              onChange={(event) =>
                void updateSettings({ fontScale: event.target.value as FontScale })
              }
            >
              <option value="sm">Small</option>
              <option value="md">Default</option>
              <option value="lg">Large</option>
              <option value="xl">Extra large</option>
            </Select>
          </Field>
          <Toggle
            label="High contrast"
            checked={settings.highContrast}
            onChange={(highContrast) => void updateSettings({ highContrast })}
          />
          <Toggle
            label="Reduce motion"
            checked={settings.reduceMotion}
            onChange={(reduceMotion) => void updateSettings({ reduceMotion })}
          />
        </Card>

        <Card className="flex flex-col gap-3">
          <CardTitle>Feedback & ideas</CardTitle>
          <p className="text-ink-500 dark:text-ink-400 text-sm">
            A board shared by everyone using Line Runner. Suggest a feature, report a problem, or
            vote for what you want next.
          </p>
          <LinkButton to="/feedback" variant="secondary" block>
            Open the board
          </LinkButton>
        </Card>

        <Card className="flex flex-col gap-1">
          <CardTitle>Experimental</CardTitle>
          <Toggle
            label="AI scene partner and coaching"
            description="Not yet available — this deployment has no AI provider configured."
            checked={false}
            onChange={() => undefined}
          />
        </Card>

        <Card className="flex flex-col gap-3">
          <CardTitle>Sync</CardTitle>
          <p className="text-ink-500 dark:text-ink-400 text-sm">
            {online ? 'Connected.' : 'Offline — rehearsals still work and will sync later.'}{' '}
            {pendingWrites > 0 ? `${pendingWrites} change(s) waiting to upload.` : 'Everything is up to date.'}
          </p>
          <Button variant="secondary" disabled={!online} onClick={() => void syncNow()}>
            Sync now
          </Button>
        </Card>

        <Card className="flex flex-col gap-3">
          <CardTitle>Account</CardTitle>
          <p className="text-sm">{user?.displayName}</p>
          <Button variant="secondary" onClick={() => void signOut()}>
            Sign out
          </Button>

          {confirmingReset ? (
            <>
              <p className="text-sm">
                Clear the offline copy on this device? Anything already synced stays on the server.
              </p>
              <div className="flex gap-3">
                <Button variant="danger" block onClick={() => void resetLocalData()}>
                  Clear local data
                </Button>
                <Button variant="secondary" block onClick={() => setConfirmingReset(false)}>
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <Button variant="ghost" onClick={() => setConfirmingReset(true)}>
              Clear offline data
            </Button>
          )}
        </Card>
      </Screen>
    </>
  );
}
